export type LinkTarget = {
  slug: string;
  title: string;
  focus_keyword?: string | null;
  keywords?: string[] | null;
};

const STOP_PHRASES = new Set(["husbil", "husvagn", "husbilar", "husvagnar", "guide", "tips"]);

/**
 * Fasta navvsidor (hubbar). Länkas automatiskt i löptexten när frasen dyker upp,
 * vilket bygger topical clusters även för framtida auto-genererade inlägg.
 */
export const HUB_TARGETS: Array<{ href: string; phrases: string[] }> = [
  { href: "/fordon", phrases: ["husbilar till salu", "husvagnar till salu", "vårt lager", "fordon i lager", "begagnad husbil", "begagnade husbilar"] },
  { href: "/reservdelar", phrases: ["reservdelar till husbil", "reservdelar", "originaldelar"] },
  { href: "/guider", phrases: ["våra guider", "köpguide", "guider för husbil"] },
  { href: "/kunskap", phrases: ["kunskapsbanken", "vår kunskapsbank"] },
  { href: "/campingtips", phrases: ["campingtips", "ställplatser i norrbotten", "campingplatser"] },
  { href: "/stallplatser-husbil", phrases: ["ställplatser", "ställplats", "övernatta med husbil", "fri camping"] },
  { href: "/ordlista", phrases: ["husbilsordlista", "ordlista"] },
  { href: "/jamfor", phrases: ["jämför husbilar", "modelljämförelse"] },
  { href: "/husbil-matchning", phrases: ["hitta rätt husbil", "husbilsmatchning"] },
  { href: "/verktyg/manadskostnad-husbil", phrases: ["månadskostnad för husbil", "månadskostnad"] },
  { href: "/verktyg/bransleforbrukning-husbil", phrases: ["bränsleförbrukning"] },
  { href: "/verktyg/avskrivning-husbil", phrases: ["värdeminskning", "avskrivning"] },
  { href: "/verktyg/korkort-husbil", phrases: ["b-körkort", "körkort för husbil"] },
  { href: "/verktyg/passar-den-hos-mig", phrases: ["passar den hos mig", "mäta uppställningsplatsen", "plats i garaget"] },
  { href: "/b96-korkort", phrases: ["b96-körkort", "utökad b-behörighet"] },
  { href: "/vardera-husbil", phrases: ["värdera din husbil", "värdera husbil", "husbilsvärdering", "marknadsvärde"] },
  { href: "/prisstatistik-husbil", phrases: ["prisstatistik", "snittpriser", "prisläget på husbilar"] },
  { href: "/prisindex", phrases: ["prisindex", "prisutveckling", "prisutvecklingen på husbilar"] },
  { href: "/salja-husbil", phrases: ["sälja husbil", "sälja din husbil", "förmedling av husbil"] },
  { href: "/salda", phrases: ["sålda fordon", "tidigare sålda husbilar"] },
  { href: "/verktyg/fuktprotokoll", phrases: ["fuktprotokoll", "12 mätpunkter", "fuktvärden"] },
  { href: "/fukttest-husbil", phrases: ["fukttest", "fuktmätning", "fuktskador"] },
  { href: "/besiktning-husbil", phrases: ["besiktning av husbil", "kontrollbesiktning"] },
  { href: "/dieselvarmare-husbil", phrases: ["dieselvärmare", "värmesystem i husbil"] },
  { href: "/solceller-husbil", phrases: ["solceller på husbil", "solceller", "litiumbatteri"] },
  { href: "/husbilsdack", phrases: ["husbilsdäck", "cp-märkta däck", "rätt lufttryck"] },
  { href: "/husbilsforsakring", phrases: ["husbilsförsäkring", "försäkring för husbil"] },
  { href: "/leasa-husbil", phrases: ["leasa husbil", "privatleasing", "leasing av husbil", "företagsleasing"] },
  { href: "/tvatta-husvagn", phrases: ["tvätta husvagnen", "tvätta husbilen"] },
  { href: "/boka-service", phrases: ["boka service", "boka verkstad", "boka tid i verkstaden"] },
  { href: "/butik", phrases: ["vår butik", "tillbehör till husbil", "utrustning till husbilen"] },
  { href: "/husbil-resa", phrases: ["reseplaneraren", "planera resan", "planera din husbilsresa"] },
  { href: "/tester", phrases: ["våra tester", "långtidstest"] },
  { href: "/erfarenheter", phrases: ["erfarenhetsrapporter", "erfarenheter från ägare"] },
  { href: "/omdomen", phrases: ["omdömen från kunder", "kundomdömen"] },
  { href: "/rapido", phrases: ["rapido", "rapido husbil"] },
  { href: "/kontakt", phrases: ["kontakta oss"] },
  { href: "/forum", phrases: ["fritidsforum", "i forumet", "vårt forum", "diskutera med andra husbilsägare"] },
];


function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Kandidatfraser för ett inlägg, längsta först (mest specifik matchning vinner). */
function phrasesFor(target: LinkTarget): string[] {
  const raw = [target.focus_keyword ?? "", ...(target.keywords ?? []), target.title];
  return Array.from(
    new Set(
      raw
        .map((p) => (p ?? "").trim())
        .filter((p) => p.length >= 7 && p.length <= 70 && !STOP_PHRASES.has(p.toLowerCase()))
    )
  ).sort((a, b) => b.length - a.length);
}

/**
 * Lägger in kontextuella interna länkar till andra blogginlägg OCH till fasta
 * navsidor (fordon, reservdelar, guider, verktyg …) på första förekomsten av
 * en relevant fras i brödtexten. Hoppar över rubriker, kodblock, citat,
 * tabeller, HTML och redan länkad text.
 */
export function autoInternalLinks(
  markdown: string,
  targets: LinkTarget[],
  opts: {
    currentSlug: string;
    maxLinks?: number;
    maxPerParagraph?: number;
    /** Extra mål, t.ex. forumtrådar: { href: "/forum/t/slug", phrases: ["…"] } */
    extraTargets?: Array<{ href: string; phrases: string[] }>;
    /** Bas för `targets` (default /blogg) — t.ex. "/kunskap" för kunskapsbanken. */
    postBasePath?: string;
  } = { currentSlug: "" }
): string {
  if (!markdown) return markdown;
  const maxLinks = opts.maxLinks ?? 12;
  const maxPerParagraph = opts.maxPerParagraph ?? 2;
  const base = (opts.postBasePath ?? "/blogg").replace(/\/+$/, "");

  // Bara rena slugs får bli länkmål. Ett inlägg vars slug/titel råkat få
  // markdown i sig (t.ex. "[fukttest](/fukttest-husbil)-…") skulle annars
  // generera en trasig URL som ger 404/500 för crawlers.
  const CLEAN_SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
  const CLEAN_PHRASE = /^[^[\]()<>{}|#*`\\]+$/;

  const postCandidates = targets
    .filter((t) => t.slug && t.slug !== opts.currentSlug && CLEAN_SLUG.test(t.slug))
    .flatMap((t) =>
      phrasesFor(t)
        .filter((phrase) => CLEAN_PHRASE.test(phrase))
        .map((phrase) => ({ phrase, key: t.slug, href: `${base}/${t.slug}` })),
    );

  const hubCandidates = [...HUB_TARGETS, ...(opts.extraTargets ?? [])].flatMap((h) =>
    h.phrases
      .map((p) => (p ?? "").trim())
      .filter(
        (p) =>
          p.length >= 7 && p.length <= 70 && !STOP_PHRASES.has(p.toLowerCase()) && CLEAN_PHRASE.test(p),
      )
      .map((phrase) => ({ phrase, key: h.href, href: h.href })),
  );


  const candidates = [...postCandidates, ...hubCandidates].sort(
    (a, b) => b.phrase.length - a.phrase.length
  );

  if (!candidates.length) return markdown;

  const lines = markdown.split("\n");
  const usedKeys = new Set<string>();
  let inFence = false;
  let linksAdded = 0;

  for (let i = 0; i < lines.length && linksAdded < maxLinks; i++) {
    const line = lines[i];
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    // Hoppa över rubriker, citat, tabeller och HTML-block
    if (/^\s*(#|>|\||<)/.test(line)) continue;
    if (line.trim().length < 40) continue;

    let updated = line;
    let inThisLine = 0;
    for (const cand of candidates) {
      if (linksAdded >= maxLinks || inThisLine >= maxPerParagraph) break;
      if (usedKeys.has(cand.key)) continue;
      const re = new RegExp(`(?<![\\[\\(\\w])(${escapeRegExp(cand.phrase)})(?![\\w\\]\\)])`, "i");
      const match = re.exec(updated);
      if (!match) continue;
      // Undvik att länka inuti en befintlig markdown-länk
      const before = updated.slice(0, match.index);
      if ((before.match(/\[/g)?.length ?? 0) !== (before.match(/\]/g)?.length ?? 0)) continue;
      // …eller inuti en URL-parentes: [text](/nagon-lank-med-frasen)
      if ((before.match(/\(/g)?.length ?? 0) !== (before.match(/\)/g)?.length ?? 0)) continue;

      // Hoppa över om frasen redan är länkad någonstans i texten
      if (updated.includes(`](${cand.href})`)) continue;
      updated = updated.replace(re, `[$1](${cand.href})`);
      usedKeys.add(cand.key);
      inThisLine++;
      linksAdded++;
    }
    lines[i] = updated;
  }

  return lines.join("\n");
}

